/**
 * Etapa 3: Importação de contatos
 * Nomear lista e iniciar processo de importação
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  Upload,
  Users,
  UserPlus,
  UserCheck,
  Clock
} from 'lucide-react';

import { CSVParseResult, RowValidation } from '@/lib/csv-utils';
import { ValidationStats, ImportProgress } from '@/hooks/useListImport';

// =====================================================
// TIPOS
// =====================================================

interface ContactImportStepProps {
  csvData: CSVParseResult | null;
  validatedRows: RowValidation[];
  validationStats: ValidationStats | null;
  listName: string;
  onListNameChange: (name: string) => void;
  isImporting: boolean;
  progress: ImportProgress | null;
  onStartImport: () => void;
}

// =====================================================
// COMPONENTE
// =====================================================

export default function ContactImportStep({
  csvData,
  validatedRows,
  validationStats,
  listName,
  onListNameChange,
  isImporting,
  progress,
  onStartImport
}: ContactImportStepProps) {
  const canStart = listName.trim().length > 0 && !isImporting;
  
  // =====================================================
  // RENDER
  // =====================================================
  
  if (isImporting && progress) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        <Loader2 className="h-16 w-16 animate-spin text-primary" />
        
        <div className="text-center space-y-2">
          <p className="text-lg font-medium">{progress.message}</p>
          <p className="text-sm text-muted-foreground">
            {progress.current} de {progress.total}
          </p>
        </div>
        
        <Progress value={(progress.current / progress.total) * 100} className="w-80" />
        
        <Alert className="max-w-md">
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Não feche esta janela. O processo pode levar alguns minutos dependendo da quantidade de contatos.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  return (
    <div 
      className="space-y-6 w-full" 
      style={{ 
        maxWidth: '100%', 
        overflow: 'hidden', 
        contain: 'inline-size' 
      }}
    >
      {/* Nome da lista */}
      <Card>
        <CardHeader>
          <CardTitle>Nomear Lista</CardTitle>
          <CardDescription>
            Dê um nome para identificar esta lista de contatos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="listName">Nome da lista *</Label>
            <Input
              id="listName"
              value={listName}
              onChange={(e) => onListNameChange(e.target.value)}
              placeholder="Ex: Clientes Dezembro 2024"
              maxLength={100}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Resumo da importação */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Resumo da Importação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <UserPlus className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{validationStats?.newContacts || 0}</p>
                <p className="text-sm text-muted-foreground">Novos contatos a criar</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <UserCheck className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{validationStats?.existingInSystem || 0}</p>
                <p className="text-sm text-muted-foreground">Contatos existentes</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{validationStats?.valid || 0}</p>
                <p className="text-sm text-muted-foreground">Total na lista</p>
              </div>
            </div>
          </div>
          
          {csvData && (
            <div className="mt-4 pt-4 border-t">
              {/* Container com scroll horizontal para muitas colunas */}
              <div 
                className="relative overflow-hidden" 
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {/* Gradiente de fade à direita */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
                
                <p 
                  className="text-sm text-muted-foreground overflow-x-auto whitespace-nowrap pb-1 pr-8 scrollbar-thin"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  <strong>Colunas:</strong> {csvData.headers.join(', ')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Aviso de expiração */}
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertDescription>
          <strong>Importante:</strong> O arquivo CSV será armazenado temporariamente por 3 dias.
          Após este período, apenas os metadados da lista serão mantidos para histórico e relatórios.
        </AlertDescription>
      </Alert>
      
      {/* Botão de iniciar */}
      <div className="flex justify-end">
        <Button 
          onClick={onStartImport} 
          disabled={!canStart}
          size="lg"
        >
          <Upload className="h-4 w-4 mr-2" />
          Iniciar Importação
        </Button>
      </div>
    </div>
  );
}
